import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { CIRCUIT_TYPE_TO_ICON, MANUFACTURER_ID_TO_EV_CHARGER_ICON } from 'clipsal-cortex-icons/src/appliance-icon-maps';

import { useGetSiteSwitches } from '../../features/scheduler/schedulerApi';
import { selectSite } from '../../features/site/siteSlice';

/**
 * Retrieve the icon for a site device based on its type. This is typed as `Device` in `api-site.ts`.
 *
 * @param siteDeviceId - The ID of the `SiteDevice`.
 * @returns The icon for the site device.
 */
export function useSiteDeviceIcon(siteDeviceId: number) {
  const { devices } = useSelector(selectSite);
  const { switches } = useGetSiteSwitches();

  return useMemo(() => {
    // currently we only support EV_CHARGER and HOT_WATER
    // so, switch is hot water
    const isSwitch = !!switches.find((s) => s.id === siteDeviceId);
    if (isSwitch) return CIRCUIT_TYPE_TO_ICON['load_hot_water'];

    const siteDevice = devices.find((d) => d.id === siteDeviceId);
    if (siteDevice) {
      const manufacturerId = siteDevice?.manufacturer_id ?? 0;
      return MANUFACTURER_ID_TO_EV_CHARGER_ICON[manufacturerId] ?? CIRCUIT_TYPE_TO_ICON['load_ev_charger'];
    } else {
      return CIRCUIT_TYPE_TO_ICON['load_other'];
    }
  }, [devices, switches, siteDeviceId]);
}
